import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ExternalLink, Layers } from "lucide-react";
import Reveal from "./Reveal";
import { INSIGHTS } from "../data/content";

const pad = (n) => String(n).padStart(2, "0");
const slidePath = (dir, i) => `/insights/${dir}/slide-${pad(i)}.png`;

function Lightbox({ post, onClose }) {
  const [idx, setIdx] = useState(1);
  const total = post.slides;

  const next = useCallback(() => setIdx((i) => (i % total) + 1), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 2 + total) % total + 1), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-[#050A14]/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-[#F2EFE9] hover:text-[#D95A41] transition-colors"
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X size={30} />
      </button>

      <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={slidePath(post.dir, idx)}
            alt={`${post.title} slide ${idx}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="w-full rounded-xl border border-white/10"
          />
        </AnimatePresence>

        <div className="mt-5 flex items-center justify-between">
          <button onClick={prev} className="p-3 rounded-full bg-white/10 hover:bg-[#D95A41] transition-colors text-[#F2EFE9]" data-testid="lightbox-prev" aria-label="Previous">
            <ChevronLeft size={22} />
          </button>
          <span className="text-sm font-500 text-[#94A3B8] tracking-widest">{pad(idx)} / {pad(total)}</span>
          <button onClick={next} className="p-3 rounded-full bg-white/10 hover:bg-[#D95A41] transition-colors text-[#F2EFE9]" data-testid="lightbox-next" aria-label="Next">
            <ChevronRight size={22} />
          </button>
        </div>
      </div>
    </motion.div>,
    document.body
  );
}

export default function Insights() {
  const [active, setActive] = useState(null);

  return (
    <section id="insights" className="relative z-10 py-28 md:py-40 max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="insights">
      <Reveal>
        <p className="text-sm font-500 tracking-[0.3em] uppercase text-[#D95A41] mb-6">Insights</p>
        <h2 className="text-4xl md:text-6xl font-900 tracking-tightest max-w-3xl leading-[1] text-[#F2EFE9]">
          Notes from the field.
        </h2>
        <p className="mt-6 max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
          Long-form thinking on in-house practice, commercial risk and building legal functions that scale.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        {INSIGHTS.map((post, i) => (
          <Reveal key={post.id} delay={i * 0.08}>
            <div className="group rounded-3xl border border-white/10 overflow-hidden bg-[#0B132B]/50 hover:border-[#D95A41]/50 transition-colors duration-300" data-testid={`insight-card-${post.id}`}>
              <div className="relative overflow-hidden cursor-pointer" onClick={() => setActive(post)}>
                <img
                  src={slidePath(post.dir, 1)}
                  alt={post.title}
                  className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#050A14]/70 backdrop-blur-sm text-xs font-500 text-[#F2EFE9]">
                  <Layers size={14} /> {post.slides} slides
                </div>
              </div>
              <div className="p-8">
                <p className="text-xs font-700 tracking-[0.2em] uppercase text-[#D95A41] mb-3">{post.kicker}</p>
                <h3 className="text-2xl font-700 text-[#F2EFE9] tracking-tight leading-snug">{post.title}</h3>
                <p className="mt-4 text-[#94A3B8] leading-relaxed">{post.excerpt}</p>
                <div className="mt-6 flex items-center gap-4">
                  <button
                    onClick={() => setActive(post)}
                    data-testid={`insight-view-${post.id}`}
                    className="px-5 py-2.5 rounded-full bg-[#D95A41] text-[#F2EFE9] text-sm font-700 hover:bg-[#E6735A] transition-colors"
                  >
                    View carousel
                  </button>
                  <a
                    href={post.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-500 text-[#94A3B8] hover:text-[#F2EFE9] transition-colors"
                    data-testid={`insight-linkedin-${post.id}`}
                  >
                    On LinkedIn <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && <Lightbox post={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
